import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, Crown, MagnifyingGlassPlus, X, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { WigglezMark } from "@/components/WigglezMark";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

export default function ArtWorkz() {
  const [art, setArt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(null);
  const { addItem } = useCart();
  const [saved, setSaved] = useState([]);
  const [lives, setLives] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { category: "artworkz" } })
      .then(({ data }) => setArt(data))
      .finally(() => setLoading(false));
    api.get("/rescues").then(({ data }) => setSaved(data)).catch(() => {});
    api.get("/donors").then(({ data }) => setLives(data.lives_saved)).catch(() => {});
  }, []);

  const buy = (p) => { addItem(p, 1, {}); toast.success(`${p.slogan} — added to bag 🐾`); };

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="artworkz-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute -left-24 top-0 h-[32rem] w-[32rem] rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,20,147,0.4) 0%, rgba(255,20,147,0) 70%)", filter: "blur(14px)" }} />
        <div className="absolute -right-24 bottom-0 h-[28rem] w-[28rem] rounded-full soul-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,229,0.35) 0%, rgba(0,229,229,0) 70%)", filter: "blur(14px)", animationDelay: "1.5s" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="overline text-primary mb-4">Mr. Wigglez ArtWorkz · Works of Art</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl max-w-4xl mx-auto">
            Art that <span className="neon-text">screams</span> for the <span style={{ color: "#ccff00" }}>voiceless.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.14 }} className="text-muted-foreground mt-5 max-w-2xl mx-auto">
            If Alec Monopoly, Timmy Sneaks and a hypercolor Jackson Pollock had a baby painter — that baby is Mr. Wigglez. Museum-grade canvases. Loud messages. Every piece funds the ranch.
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-zinc-900 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="artworkz-grid">
            {art.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="group" data-testid={`art-card-${p.id}`}>
                {/* Framed canvas */}
                <div className="relative rounded-lg overflow-hidden bg-black p-3 border-2 border-white/15 shadow-2xl" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
                  <div className="relative overflow-hidden rounded-sm">
                    <img src={(p.images || [])[0]} alt={p.name} className="aspect-square w-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                    <button onClick={() => setZoom(p)} className="absolute bottom-3 right-3 h-11 w-11 grid place-items-center rounded-full bg-black/60 backdrop-blur border border-primary/40 text-primary opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`art-zoom-${p.id}`} title="View up close">
                      <MagnifyingGlassPlus size={18} />
                    </button>
                    {p.exclusivity && (
                      <span className="absolute top-3 left-3 overline px-3 py-1 rounded-full text-black font-semibold inline-flex items-center gap-1" style={{ background: "linear-gradient(90deg,#fbbf24,#d97706)" }}>
                        <Crown weight="fill" size={11} /> {p.exclusivity}
                      </span>
                    )}
                    <div className="absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/55 backdrop-blur-sm border border-white/10"><WigglezMark size="text-[11px]" /></div>
                  </div>
                </div>
                <div className="mt-4 px-1">
                  <p className="overline text-muted-foreground mb-1 inline-flex items-center gap-1.5"><SealCheck weight="fill" size={12} className="text-accent" /> Signed & numbered · Mr. Wigglez</p>
                  <h3 className="font-display font-black uppercase text-2xl leading-tight">{p.slogan}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{p.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xl font-modern">${p.price.toLocaleString()}</span>
                    <button onClick={() => buy(p)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-[10px] font-modern hover:bg-primary/90 transition-colors" data-testid={`art-buy-${p.id}`}>
                      <PawPrint weight="fill" size={15} /> Collect
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lives Saved — transformation gallery */}
      {saved.length > 0 && (
        <section className="border-t border-white/10 bg-[#080808]" data-testid="lives-saved-gallery">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
            <p className="overline text-secondary mb-3 inline-flex items-center gap-2"><PawPrint weight="fill" size={14} /> Lives Saved · The Transformations</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-3xl sm:text-5xl mb-3">
              From the streets to <span className="neon-text">the pack.</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mb-10">
              Slide each frame. This is what your canvas, your hoodie, your donation actually buys{lives != null ? ` — ${lives.toLocaleString()} saved and climbing` : ""}.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {saved.map((d) => (
                <div key={d.name} data-testid={`lives-saved-${d.name.toLowerCase()}`}>
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-white/10">
                    {d.before_image ? (
                      <BeforeAfterSlider before={d.before_image} after={d.after_image || d.image} showLabels={false} testid={`lives-slider-${d.name.toLowerCase()}`} />
                    ) : (<img src={d.image} alt={d.name} className="h-full w-full object-cover" />)}
                    <span className="absolute top-3 right-3 z-30 overline px-3 py-1 rounded-full bg-secondary text-black inline-flex items-center gap-1 pointer-events-none"><SealCheck weight="fill" size={12} /> Saved</span>
                  </div>
                  <p className="font-display font-black uppercase text-xl mt-3">{d.name}</p>
                  <p className="text-xs text-muted-foreground">{d.breed} · {d.city === "nyc" ? "NYC" : d.city === "vegas" ? "Vegas" : "Miami"}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Zoom lightbox */}
      <AnimatePresence>
        {zoom && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/92 backdrop-blur grid place-items-center p-4" onClick={() => setZoom(null)} data-testid="art-zoom-modal">
            <button className="absolute top-5 right-5 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" data-testid="art-zoom-close"><X size={20} /></button>
            <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={(zoom.images || [])[0]} alt={zoom.name} className="w-full max-h-[78vh] object-contain rounded-lg border-2 border-white/15" />
              <p className="text-center overline text-primary mt-4">{zoom.slogan} · {zoom.exclusivity || "Open edition"} · ${zoom.price.toLocaleString()}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
